import moment from 'moment';
import * as React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { axiosInstance } from '@/axios';
import { useConstructSearchQueryParams } from '@/hooks/useConstructSearchQueryParams';
import { useShowNoDataTabs } from '@/hooks/useShowNoDataTabs';
import type { CategoryTabsResponseType, RouteParam } from '@/types';

import { useConfigUI } from './useFeatureConfig';

const fetcher = async (
  searchURL: string,
  params: Record<string, unknown>,
  signal?: AbortSignal
) => {
  const todayDate = moment().format('DD-MM-YYYY');

  return await axiosInstance.post<CategoryTabsResponseType>(
    searchURL,
    {
      type: 'category',
      num_docs: 0,
      today_date_range: {
        start_date: todayDate,
        end_date: todayDate,
      },
      ...params,
    },
    {
      signal,
    }
  );
};

const CATEGORY_TABS = ['categoryTabs'];

export const useRefetchAllFeedsCategoryTabs = () => {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries(CATEGORY_TABS, {
      refetchInactive: true,
    });
  };
};

export const useRefetchCategoryTabs = () => {
  const queryClient = useQueryClient();

  return React.useCallback(
    (boardId = 'home') => {
      return queryClient.invalidateQueries([...CATEGORY_TABS, boardId], {
        refetchInactive: true,
      });
    },
    [queryClient]
  );
};

const useEnableCategoryTabs = () => {
  const { category } = useParams<RouteParam>();

  if (['notebooks'].includes(category) || category === 'needlbox') {
    return false;
  }

  return true;
};

export const useFetchCategoryTabsImpl = (options: UseFetchCategoryType) => {
  const { board_id, channel_id, q, spell_correct, localSearchQueryParam } =
    useConstructSearchQueryParams();
  const { category, source } = useParams<RouteParam>();
  const isCategoryTabsEnabled = useEnableCategoryTabs();
  const searchURL = ['apps', 'needlbox'].includes(category)
    ? `/search/${source}`
    : `/search`;

  return useQuery(
    [
      ...CATEGORY_TABS,
      board_id,
      channel_id,
      searchURL,
      q,
      {
        search_type: localSearchQueryParam.search_type,
        timezone: localSearchQueryParam.timezone,
      },
      spell_correct,
    ],
    ({ signal }) =>
      fetcher(
        searchURL,
        {
          board_id,
          channel_id,
          q,
          search_type: localSearchQueryParam.search_type,
          timezone: localSearchQueryParam.timezone,
          spell_correct,
        },
        signal
      ),
    {
      refetchOnMount: false,
      enabled: options.enabled || isCategoryTabsEnabled,
    }
  );
};

export const useFetchCategoryTabs = (
  options: UseFetchCategoryType = { enabled: false }
) => {
  const showNoDataTabs = useShowNoDataTabs();

  const {
    isLoading: isCategoryTabsLoading,
    isError: isCategoryTabsError,
    data: categoryTabsResponse,
  } = useFetchCategoryTabsImpl(options);

  const {
    isLoading: isConfigUILoading,
    isError: isConfigUIError,
    data: config,
  } = useConfigUI();

  const data = React.useMemo(() => {
    const categoryTabs = categoryTabsResponse?.data?.category_tabs;

    if (!categoryTabs) {
      return [];
    }

    return categoryTabs.filter((tab) => {
      switch (tab.key) {
        case 'comments':
          return config?.comments;
        case 'webclips':
          return showNoDataTabs || tab.data;
        default: {
          if (!tab.data && !showNoDataTabs) {
            return false;
          }
          return true;
        }
      }
    });
  }, [categoryTabsResponse, showNoDataTabs, config]);

  return {
    isLoading: isCategoryTabsLoading || isConfigUILoading,
    isError: isCategoryTabsError || isConfigUIError,
    data,
    count: categoryTabsResponse?.data?.count,
  };
};

type UseFetchCategoryType = {
  enabled: boolean;
};
